// this vendor bundle only side-effects `window.JsBarcode`, it has no module.exports/export default
import "@/vendor/jsbarcode/JsBarcode.code128.min";

const FNSKU_PRINT_CSS = `
  @page { size: 60mm 30mm; margin: 0; }
  @media print {
    html, body { width: 60mm; height: 30mm; margin: 0; padding: 0; }
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    font-family: Arial, sans-serif;
  }
  body {
    width: 60mm;
  }
  .fnsku-label {
    width: 60mm;
    height: 30mm;
    padding: 1.8mm 2.2mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
    text-align: center;
    page-break-after: always;
    break-after: page;
  }
  .fnsku-label img.fnsku-barcode {
    display: block;
    width: 100%;
    height: auto;
    max-height: 18mm;
  }
  .fnsku-separator {
    justify-content: center;
  }
  .fnsku-cut {
    width: 100%;
    border-top: 1.2mm dashed #000;
  }
  .fnsku-code {
    width: 100%;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    line-height: 1.1;
  }
  .fnsku-title {
    width: 100%;
    font-size: 9px;
    line-height: 1.15;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }
`;

export const getPrintQuantity = (value, fallback = 1) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const escapePrintHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

export const getEffectiveFnsku = (item) => (item?.fnsku || "").toString().trim();

// Rendered on a canvas in the main document (where JsBarcode is known to work), then embedded
// as a plain <img> data URL — avoids cross-document SVG rendering issues inside the print iframe.
const renderBarcodeDataUrl = (value) => {
  try {
    const JsBarcode = window.JsBarcode;
    if (typeof JsBarcode !== "function") throw new Error("JsBarcode library failed to load");
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: "CODE128",
      displayValue: false,
      margin: 0,
      width: 2,
      height: 80,
      background: "#ffffff",
      lineColor: "#000000",
    });
    return canvas.toDataURL("image/png");
  } catch (err) {
    console.error(err);
    return "";
  }
};

export async function printFnskuLabels(labels) {
  const barcodes = new Map(); // one canvas render per distinct FNSKU, not per printed label
  const barcodeFor = (fnsku) => {
    if (!barcodes.has(fnsku)) barcodes.set(fnsku, renderBarcodeDataUrl(fnsku));
    return barcodes.get(fnsku);
  };
  const expandedLabels = labels.flatMap(label =>
    Array.from({ length: getPrintQuantity(label.quantity) }, () => ({
      fnsku: label.fnsku?.toString().trim(),
      title: label.title,
    })),
  ).filter(label => label.fnsku)
    .map(label => ({ ...label, barcodeSrc: barcodeFor(label.fnsku) }));

  if (expandedLabels.length === 0) return 0;

  // a separator label goes between different FNSKUs so the stacks are easy to tell apart
  const sheetLabels = expandedLabels.flatMap((label, i) =>
    i > 0 && expandedLabels[i - 1].fnsku !== label.fnsku ? [{ separator: true }, label] : [label],
  );

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      iframe.remove();
    }, 1000);
  };

  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc || !iframe.contentWindow) throw new Error("Print frame unavailable");

    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8" /><title>FNSKU Labels</title><style>${FNSKU_PRINT_CSS}</style></head><body>${sheetLabels.map(label => label.separator ? `
      <section class="fnsku-label fnsku-separator"><div class="fnsku-cut"></div></section>
    ` : `
      <section class="fnsku-label">
        ${label.barcodeSrc ? `<img class="fnsku-barcode" src="${label.barcodeSrc}" alt="" />` : ""}
        <div class="fnsku-code">${escapePrintHtml(label.fnsku)}</div>
        <div class="fnsku-title">${escapePrintHtml(label.title)}</div>
      </section>
    `).join("")}</body></html>`);
    doc.close();

    // wait for the embedded barcode images to finish decoding before printing
    await new Promise(resolve => {
      const imgs = Array.from(doc.images || []);
      if (imgs.length === 0) {
        resolve();
        return;
      }
      let remaining = imgs.length;
      const markDone = () => {
        remaining -= 1;
        if (remaining <= 0) resolve();
      };
      imgs.forEach(img => {
        if (img.complete) {
          markDone();
        } else {
          img.addEventListener("load", markDone, { once: true });
          img.addEventListener("error", markDone, { once: true });
        }
      });
    });

    iframe.contentWindow.onafterprint = cleanup;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    return expandedLabels.length;
  } catch (error) {
    cleanup();
    throw error;
  }
}
